/**
 * Starter Sidebar — vertical nav rail matching preview/comp-sidebar.html.
 *
 * Composition uses compound sub-components so consumers compose freely:
 *
 *   <Sidebar>
 *     <Sidebar.Brand>Starter</Sidebar.Brand>
 *     <Sidebar.Section label="Workflow">
 *       <Sidebar.Item icon={<Inbox />} active>Inbox <Sidebar.Count>12</Sidebar.Count></Sidebar.Item>
 *       <Sidebar.Item icon={<Users />}>Projects</Sidebar.Item>
 *     </Sidebar.Section>
 *     <Sidebar.Footer>
 *       <Sidebar.User name="Mathew Mathew" role="Member" />
 *     </Sidebar.Footer>
 *   </Sidebar>
 *
 * Pure CSS-Module — no router coupling. Pass `as` / `href` / `onClick` on
 * `<Sidebar.Item>` to wire it into TanStack Router via the `component={Link}`
 * pattern.
 */
import {
  forwardRef,
  type AnchorHTMLAttributes,
  type ElementType,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
} from 'react';
import styles from './Sidebar.module.css';

export interface SidebarProps extends HTMLAttributes<HTMLElement> {
  /**
   * Icons-only mode: labels + section eyebrows fade out and items center their
   * icon. Drives the `data-collapsed` attribute the CSS keys off, so the labels
   * stay in the DOM (and animate) rather than unmounting. Desktop-rail only.
   */
  readonly collapsed?: boolean;
  readonly children?: ReactNode;
}

interface BrandProps extends HTMLAttributes<HTMLDivElement> {
  readonly logo?: ReactNode;
  readonly children?: ReactNode;
}
function Brand({ logo, className, children, ...rest }: BrandProps): ReactElement {
  return (
    <div
      className={[styles.brand, className].filter(Boolean).join(' ')}
      {...rest}
    >
      {logo !== undefined ? <span className={styles.brandLogo}>{logo}</span> : null}
      <span className={styles.brandWordmark}>{children}</span>
    </div>
  );
}

interface SectionProps extends HTMLAttributes<HTMLDivElement> {
  readonly label?: ReactNode;
  readonly children?: ReactNode;
}
function Section({ label, className, children, ...rest }: SectionProps): ReactElement {
  return (
    <div
      className={[styles.section, className].filter(Boolean).join(' ')}
      {...rest}
    >
      {label !== undefined ? <span className={styles.sectionLabel}>{label}</span> : null}
      <ul className={styles.itemList}>{children}</ul>
    </div>
  );
}

interface ItemProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'children'> {
  readonly icon?: ReactNode;
  /** Marks the active nav state — paints --selected-bg + --selected-fg. */
  readonly active?: boolean;
  /**
   * Marks the item as non-interactive — paints --fg-disabled, removes hover/active
   * affordances, sets `aria-disabled` and `tabIndex={-1}` so it's skipped by keyboard
   * nav. Overrides `active` styling. The CSS also styles any item with
   * `aria-disabled="true"` directly, so passing that attribute works too.
   */
  readonly disabled?: boolean;
  /** Element override — defaults to `<a>` so it can be polymorphic via `as={Link}`. */
  readonly as?: ElementType;
  /**
   * Navigation target when rendered as a router `<Link>` (`as={Link} to="...">`).
   * Loosely typed as `string` so this UI primitive stays router-agnostic; the
   * call site passes a route-typed value. Forwarded to the `as` element.
   */
  readonly to?: string;
  readonly children?: ReactNode;
}
function Item({
  icon,
  active = false,
  disabled = false,
  as: Tag = 'a',
  className,
  children,
  ...rest
}: ItemProps): ReactElement {
  const classes = [
    styles.item,
    active && !disabled ? styles.itemActive : undefined,
    className,
  ]
    .filter(Boolean)
    .join(' ');
  const Component = Tag;
  return (
    <li>
      <Component
        className={classes}
        {...(active && !disabled ? { 'aria-current': 'page' as const } : {})}
        {...(disabled ? { 'aria-disabled': true as const, tabIndex: -1 } : {})}
        {...rest}
      >
        {icon !== undefined ? <span className={styles.itemIcon}>{icon}</span> : null}
        <span className={styles.itemLabel}>{children}</span>
        {/* Collapsed-rail caption: a SECOND label node stacked under the icon in the
            icons-only rail. Kept separate from `.itemLabel` (not the same element
            repositioned) so the two cross-fade on collapse/expand rather than one
            morphing into the other. aria-hidden + pointer-events:none so it never
            double-announces or intercepts clicks; the beside-icon label + link text
            already name the item. Absolutely positioned (see CSS) so it adds no row
            height — the row stays dimensionally identical between the two states. */}
        <span className={styles.itemLabelStacked} aria-hidden="true">
          {children}
        </span>
      </Component>
    </li>
  );
}

interface CountProps extends HTMLAttributes<HTMLSpanElement> {
  readonly children?: ReactNode;
}
function Count({ className, children, ...rest }: CountProps): ReactElement {
  return (
    <span
      className={[styles.count, className].filter(Boolean).join(' ')}
      {...rest}
    >
      {children}
    </span>
  );
}

interface FooterProps extends HTMLAttributes<HTMLDivElement> {
  readonly children?: ReactNode;
}
function Footer({ className, children, ...rest }: FooterProps): ReactElement {
  return (
    <div
      className={[styles.footer, className].filter(Boolean).join(' ')}
      {...rest}
    >
      {children}
    </div>
  );
}

interface UserProps extends Omit<HTMLAttributes<HTMLDivElement>, 'role'> {
  readonly name: ReactNode;
  /** Subtitle text — typically the user's role ("Member"). Named `role` here. */
  readonly role?: ReactNode;
  /** Optional avatar slot — pass `<Avatar tone="green" initials="MM" />`. */
  readonly avatar?: ReactNode;
}
function User({ name, role, avatar, className, ...rest }: UserProps): ReactElement {
  return (
    <div
      className={[styles.user, className].filter(Boolean).join(' ')}
      {...rest}
    >
      {avatar !== undefined ? avatar : null}
      <div className={styles.userText}>
        <span className={styles.userName}>{name}</span>
        {role !== undefined ? (
          <span className={styles.userRole}>{role}</span>
        ) : null}
      </div>
    </div>
  );
}

const SidebarWithRef = forwardRef<HTMLElement, SidebarProps>(function Sidebar(
  { className, collapsed = false, children, ...rest },
  ref,
) {
  return (
    <nav
      ref={ref}
      aria-label="Primary"
      className={[styles.root, className].filter(Boolean).join(' ')}
      data-collapsed={collapsed ? 'true' : undefined}
      {...rest}
    >
      {children}
    </nav>
  );
});

export const Sidebar = Object.assign(SidebarWithRef, {
  Brand,
  Section,
  Item,
  Count,
  Footer,
  User,
});
